/* LCT — Tiroir de filtres (côté gauche)
   - Ouverture : tout bouton [data-lct-fd-open]
   - Chaque changement met à jour la grille en direct (Section Rendering API)
   - Indépendant de facets.js (les filtres Dawn restent en place) */
if (!customElements.get('lct-filter-drawer')) {
    class LctFilterDrawer extends HTMLElement {
        connectedCallback() {
            // Sortir le tiroir de la section (évite les soucis de position: fixed / z-index)
            if (this.parentElement !== document.body) {
                document.querySelectorAll('body > lct-filter-drawer').forEach((el) => el !== this && el.remove());
                document.body.appendChild(this);
                return; // connectedCallback est rappelé après le déplacement
            }
            if (this.dataset.ready) return;
            this.dataset.ready = 'true';

            this.sectionId = this.dataset.sectionId;
            this.renderedSearch = window.location.search;
            this.controller = null;
            this.debounceTimer = null;

            this.addEventListener('click', this.onClick.bind(this));
            this.addEventListener('change', this.onChange.bind(this));
            this.addEventListener('keydown', (e) => {
                if (e.key === 'Escape') this.close();
                if (e.key === 'Tab') this.trapFocus(e);
                if (e.key === 'Enter' && e.target.matches('[data-lct-fd-price]')) {
                    e.preventDefault();
                    this.submit(0);
                }
            });

            document.addEventListener('click', (e) => {
                const opener = e.target.closest('[data-lct-fd-open]');
                if (opener) {
                    e.preventDefault();
                    this.open(opener);
                    return;
                }
                // Filtres actifs affichés dans la barre, hors du tiroir
                const link = e.target.closest('#LctFilterBar [data-lct-fd-link]');
                if (link) {
                    e.preventDefault();
                    const url = new URL(link.href, window.location.origin);
                    url.searchParams.delete('section_id');
                    url.searchParams.delete('page');
                    const def = this.querySelector('#LctFilterForm')?.dataset.defaultSort;
                    if (url.searchParams.get('sort_by') === def) url.searchParams.delete('sort_by');
                    this.render(url.searchParams.toString());
                }
            });

            // Retour arrière du navigateur : on resynchronise à la prochaine ouverture
            window.addEventListener('popstate', () => {
                if (!this.hidden) this.render(window.location.search.slice(1), false);
            });
        }

        open(opener) {
            this.opener = opener;
            clearTimeout(this.closeTimer);
            this.hidden = false;
            document.documentElement.classList.add('lct-fd-lock');
            requestAnimationFrame(() => this.classList.add('is-open'));
            document.querySelectorAll('[data-lct-fd-open]').forEach((b) => b.setAttribute('aria-expanded', 'true'));
            this.querySelector('.lct-fd__close')?.focus({ preventScroll: true });

            // Filtres modifiés ailleurs (anciens filtres Dawn) : on rafraîchit le tiroir
            if (window.location.search !== this.renderedSearch) {
                this.render(window.location.search.slice(1), false);
            }
        }

        close() {
            if (this.hidden) return;
            this.classList.remove('is-open');
            document.documentElement.classList.remove('lct-fd-lock');
            document.querySelectorAll('[data-lct-fd-open]').forEach((b) => b.setAttribute('aria-expanded', 'false'));
            clearTimeout(this.closeTimer);
            this.closeTimer = setTimeout(() => {
                this.hidden = true;
            }, 350);
            (this.opener && document.body.contains(this.opener)
                ? this.opener
                : document.querySelector('[data-lct-fd-open]')
            )?.focus({ preventScroll: true });
        }

        onClick(e) {
            if (e.target.closest('[data-lct-fd-close]')) {
                e.preventDefault();
                this.close();
                return;
            }
            const summary = e.target.closest('.lct-fd__summary');
            if (summary) {
                e.preventDefault();
                this.toggleGroup(summary.parentElement);
                return;
            }
            const more = e.target.closest('[data-lct-fd-more]');
            if (more) {
                more.closest('.lct-fd__values').classList.toggle('is-expanded');
                return;
            }
            const link = e.target.closest('[data-lct-fd-link]');
            if (link) {
                e.preventDefault();
                const url = new URL(link.href, window.location.origin);
                url.searchParams.delete('section_id');
                url.searchParams.delete('page');
                const def = this.querySelector('#LctFilterForm')?.dataset.defaultSort;
                if (url.searchParams.get('sort_by') === def) url.searchParams.delete('sort_by');
                this.render(url.searchParams.toString());
            }
        }

        // Accordéons : ouverture / fermeture animées
        toggleGroup(details) {
            const content = details.querySelector('.lct-fd__summary + *');
            const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
            if (!content || reduce || !content.animate) {
                details.open = !details.open;
                return;
            }
            details.currentAnimation?.cancel();
            const closing = details.open;
            if (!closing) details.open = true;
            const height = content.offsetHeight;
            const frames = [
                { height: '0px', opacity: 0 },
                { height: `${height}px`, opacity: 1 },
            ];
            content.style.overflow = 'hidden';
            const anim = content.animate(closing ? frames.reverse() : frames, {
                duration: 250,
                easing: 'ease-out',
            });
            details.currentAnimation = anim;
            anim.onfinish = () => {
                if (closing) details.open = false;
                content.style.overflow = '';
                details.currentAnimation = null;
            };
            anim.oncancel = () => {
                content.style.overflow = '';
            };
        }

        onChange(e) {
            if (!e.target.closest('#LctFilterForm')) return;
            // Prix : on attend la fin de saisie ; cases à cocher : quasi immédiat
            this.submit(e.target.matches('[data-lct-fd-price]') ? 600 : 250);
        }

        submit(delay) {
            clearTimeout(this.debounceTimer);
            this.debounceTimer = setTimeout(() => {
                const form = this.querySelector('#LctFilterForm');
                const params = new URLSearchParams();
                for (const [key, value] of new FormData(form)) {
                    if (value === '') continue;
                    if (key === 'sort_by' && value === form.dataset.defaultSort) continue;
                    params.append(key, value);
                }
                this.render(params.toString());
            }, delay);
        }

        async render(searchParams, updateUrl = true) {
            if (this.controller) this.controller.abort();
            this.controller = new AbortController();

            const grid = document.querySelector('#ProductGridContainer .collection');
            grid?.classList.add('loading');
            this.classList.add('is-loading');

            const url = `${window.location.pathname}?section_id=${this.sectionId}${searchParams ? '&' + searchParams : ''}`;
            let html;
            try {
                const res = await fetch(url, { signal: this.controller.signal });
                html = new DOMParser().parseFromString(await res.text(), 'text/html');
            } catch (err) {
                if (err.name !== 'AbortError') {
                    // En cas d'erreur, on recharge la page filtrée
                    window.location.search = searchParams;
                }
                return;
            }

            // État à conserver : groupes ouverts, "voir plus", scroll, focus
            const body = this.querySelector('#LctFilterBody');
            const openIds = [...this.querySelectorAll('details.lct-fd__group[open]')].map((d) => d.id);
            const closedIds = [...this.querySelectorAll('details.lct-fd__group:not([open])')].map((d) => d.id);
            const expanded = [...this.querySelectorAll('.lct-fd__values.is-expanded')].map(
                (v) => v.closest('details')?.id
            );
            const scrollTop = body.scrollTop;
            const focusedId = document.activeElement?.closest('.lct-fd__option')?.id;

            // 1. Contenu du tiroir
            ['LctFilterBody', 'LctFilterFooter'].forEach((id) => {
                const source = html.getElementById(id);
                const target = document.getElementById(id);
                if (source && target) target.innerHTML = source.innerHTML;
            });
            openIds.forEach((id) => document.getElementById(id)?.setAttribute('open', ''));
            closedIds.forEach((id) => document.getElementById(id)?.removeAttribute('open'));
            expanded.forEach((id) => document.getElementById(id)?.querySelector('.lct-fd__values')?.classList.add('is-expanded'));
            body.scrollTop = scrollTop;
            if (focusedId) document.getElementById(focusedId)?.querySelector('input')?.focus({ preventScroll: true });

            // 2. Barre, grille et anciens filtres Dawn (pour rester synchronisés)
            ['LctFilterBar', 'ProductGridContainer', 'main-collection-filters'].forEach((id) => {
                const source = html.getElementById(id);
                const target = document.getElementById(id);
                if (source && target) target.innerHTML = source.innerHTML;
            });
            document
                .querySelectorAll('#ProductGridContainer .scroll-trigger')
                .forEach((el) => el.classList.add('scroll-trigger--cancel'));

            this.classList.remove('is-loading');

            const search = searchParams ? `?${searchParams}` : '';
            if (updateUrl) history.pushState({ searchParams }, '', `${window.location.pathname}${search}`);
            this.renderedSearch = search;
        }

        trapFocus(e) {
            const focusables = [
                ...this.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), summary'),
            ].filter((el) => el.offsetParent !== null);
            if (!focusables.length) return;
            const first = focusables[0];
            const last = focusables[focusables.length - 1];
            if (e.shiftKey && document.activeElement === first) {
                e.preventDefault();
                last.focus();
            } else if (!e.shiftKey && document.activeElement === last) {
                e.preventDefault();
                first.focus();
            }
        }
    }

    customElements.define('lct-filter-drawer', LctFilterDrawer);
}